<template>
	<v-app>
		<loading :parentLoading="$store.getters.isLoading" />
		<app-header />
		<tool-panel v-if="isSitePage" @show-ai-chatbot="showAiChatbot = true" />
		<ai-chatbot v-if="isSitePage && temporaryRoadAiEnabled" @close-ai-chatbot="closeAiChatbot"
			@road-ai-creation="roadAICreation" @re-road-ai-creation="reRoadAiCreation" />
		<road-proposal-list-panel v-if="isSitePage" />
		<v-main :style="$route.path === '/top' ? 'padding-left:50vw' : ''">
			<resizable-splitter :initial-top-percentage="isSitePage ? 60 : 100" :enable-drag="isSitePage"
				@resize-end="handleSplitterResize" @window-resize="triggerAll">
				<template #top>
					<cesium-container key="cesium-instance" />
				</template>
				<template #bottom>
					<router-view v-if="isSitePage" />
				</template>
			</resizable-splitter>
		</v-main>
		<router-view v-if="!isSitePage" />
		<v-snackbar v-for="(snackbar, index) in snackbars" :z-index="aboveOverlayIndex" :key="snackbar.id"
			v-model="snackbar.showing" :timeout="snackbar.timeout" location="top right" :color="snackbar.color" rounded="pill"
			:style="`top: ${notification_top_list[index]}px;`" class="text-pre-wrap" content-class="snackbar-contents"
			:ref="snackbar.id">
			<template v-slot:text>
				<span class="ml-5" style="color: white" v-text="snackbar.text"></span>
			</template>
			<template v-slot:actions>
				<v-btn icon @click="close_snackbar(snackbar)">
					<v-icon color="white">mdi-check</v-icon>
				</v-btn>
			</template>
		</v-snackbar>
		<v-dialog v-model="notificationPopup.isShow" max-width="500px" :z-index="aboveOverlayIndex" :scrim="false">
			<notification-popup :isShow="notificationPopup.isShow" :title=notificationPopup.title
				:message=notificationPopup.message :buttonText=notificationPopup.buttonText
				@close="updateNotificationPopup(false)"></notification-popup>
		</v-dialog>
	</v-app>
</template>

<script>
import { fetchAccessControlV2 } from "@/api/startupApi";
import Loading from "@/components/common/LoadingOverLay.vue";
import { selectByMigration } from "@/config/v2Migration";
import { triggerAll } from "@/utils/chartViewUpdaters";
import axios from "axios";
import { reactive } from "vue";
import { mapMutations, mapState } from "vuex";
import AppHeader from "./components/AppHeader.vue";
import CesiumContainer from "./components/CesiumContainer.vue";
import AiChatbot from "./components/Project/AiChatbot.vue";
import RoadProposalListPanel from "./components/Project/RoadProposalListPanel.vue";
import ToolPanel from "./components/Project/ToolPanel.vue";
import NotificationPopup from "./components/common/NotificationPopup.vue";
import ResizableSplitter from "./components/common/ResizableSplitter.vue";
import { startWorker } from "./utils/cesium-common";
import localDb from "./utils/local-db";
import * as roadAIUtil from "./utils/roadAIUtils";
import * as wsConvertToPointCloudCRS from "./utils/wsConvertToPointCloudCRS";

export default {
	name: "App",
	components: {
		AppHeader,
		CesiumContainer,
		ResizableSplitter,
		Loading,
		NotificationPopup,
		ToolPanel,
		AiChatbot,
		RoadProposalListPanel,
	},
	async beforeCreate() {
		window.CESIUM_BASE_URL = CESIUM_BASE_URL;
		// reactive() so console mutations (e.g. window.debugFlag.wallThickness = true) trigger Vue re-renders
		window.debugFlag = reactive(DEBUG_FLAG);
		// reactive() so the async /access-control merge below re-renders
		// components reading the flags (the release dist bakes no flag values,
		// so every flag arrives through that merge).
		window.featureFlag = reactive({ ...FEATURE_FLAG });
		await this.$store.dispatch("getUser");

		try {
			const accessInfo = await selectByMigration("startup", {
				v1: () =>
					axios
						.get(`${import.meta.env.VITE_API_BASE}/access-control`, {
							auth: this.$store.state.authObject,
						})
						.then((res) => res.data),
				v2: fetchAccessControlV2,
			})();

			const accessFlags = accessInfo.featureAccess;

			const allKeys = new Set([
				...Object.keys(window.featureFlag || {}),
				...Object.keys(accessFlags || {}),
			]);

			for (const key of allKeys) {
				const a = Boolean(window.featureFlag?.[key]);
				const b = Boolean(accessFlags?.[key]);
				window.featureFlag[key] = a || b;
			}
		} catch (error) {
			console.error("Failed to fetch access control information:", error);
		}
	},
	mounted() {
		// HACK: ダイアログのステータスはstore等vueの機能を使用して管理すべきだが他の実装と揃えるため一旦イベント発火方式にする
		window.addEventListener("openNotificationPopup", (event) =>
			this.updateNotificationPopup(true, event),
		);

		window.onbeforeunload = () => {
			// clear cookie
			document.cookie =
				"login_auth=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
		};

		const wsServerUrl =
			import.meta.env.VITE_WEB_SOCKET_URL + "/wsPointCloudTransformer";

		wsConvertToPointCloudCRS.initWebSocket(wsServerUrl);
		startWorker();
		localDb.setUpDB();
	},
	data: () => ({
		code: null,
		session_state: null,
		notification_top_list: [],
		aboveOverlayIndex: 10000, // v-loadingのz-indexより優先する
		showAiChatbot: false,
		notificationPopup: {
			isShow: false,
			title: undefined,
			message: undefined,
			buttonText: undefined,
		},
	}),
	computed: {
		lang: function () {
			return this.$store.getters["lang"];
		},
		isSitePage() {
			return !!this.$route.query.siteId;
		},
		temporaryRoadAiEnabled() {
			return window.featureFlag?.temporaryRoadAi ?? false;
		},
		...mapState(["snackbars"]),
	},
	watch: {
		lang() {
			this.$nextTick(() => {
				this.$i18n.locale = this.$store.state.lang;
			});
		},
		snackbars: {
			handler() {
				if (this.notification_top_list.length != this.snackbars.length) {
					// ref access needs to be done after DOM update
					this.$nextTick(() => {
						this.notification_top_list = [];
						for (let i = 0; i < this.snackbars.length; i++) {
							let top = 0;
							for (let j = 0; j < i; j++) {
								const ref = this.$refs[this.snackbars[j].id];
								// check ref existence because snackbar may be closed already
								if (ref && ref[0] && ref[0].contentEl) {
									top += ref[0].contentEl.clientHeight + 10;
								}
							}
							this.notification_top_list.push(top);
						}
					});
				}
			},
			deep: true,
		},
	},
	methods: {
		...mapMutations(["clear_snackbars"]),
		close_snackbar(snackbar) {
			snackbar.showing = false;
			this.clear_snackbars();
		},
		handleSplitterResize(data) {
			this.$store.dispatch("requestViewerRender");
		},
		triggerAll,
		updateNotificationPopup(isShow, $event) {
			if (isShow) {
				// 表示
				this.notificationPopup.isShow = true;
				this.notificationPopup.title = $event.detail.title;
				this.notificationPopup.message = $event.detail.message;
				this.notificationPopup.buttonText = $event.detail.buttonText;
			} else {
				// 非表示
				this.notificationPopup.isShow = false;
				this.notificationPopup.title = undefined;
				this.notificationPopup.message = undefined;
				this.notificationPopup.buttonText = undefined;
			}
		},
		roadAICreation() {
			this.$store.dispatch("toolPanelActions/triggerAction", "roadAICreation");
		},
		reRoadAiCreation() {
			this.$store.dispatch(
				"toolPanelActions/triggerAction",
				"reRoadAiCreation",
			);
		},
		closeAiChatbot() {
			this.showAiChatbot = false;
			const isStartAndEndPoint = this._hasEmptyValues();
			if (isStartAndEndPoint) {
				window["viewer"].cancelRoadCreation();
			} else {
				roadAIUtil.resetDefaultValue(this.$store);
			}
		},
		_hasEmptyValues() {
			const points = roadAIUtil.getStartEndPoint(this.$store);
			for (const key in points) {
				if (points[key] === undefined) {
					return true;
				}
			}
			return false;
		},
	},
};
</script>

<style></style>
